import { isSameDay } from 'date-fns';
import type { CalendarEvent } from '@wrapforce/shared';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';
import { colorForJob } from './colors';

const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 19;
const HOUR_HEIGHT = 56;
const PX_PER_MIN = HOUR_HEIGHT / 60;

function hourRangeFor(day: Date, events: CalendarEvent[]): { startHour: number; endHour: number } {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  for (const e of events) {
    const start = new Date(e.scheduledStart);
    const end = e.scheduledEnd ? new Date(e.scheduledEnd) : start;
    if (isSameDay(start, day)) startHour = Math.min(startHour, start.getHours());
    if (isSameDay(end, day)) endHour = Math.max(endHour, end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours());
  }
  return { startHour, endHour: Math.max(endHour, startHour + 1) };
}

export function DayTimeline({
  day, events, onSelectEvent,
}: {
  day: Date;
  events: CalendarEvent[];
  onSelectEvent: (event: CalendarEvent) => void;
}) {
  const { formatDate } = useLocaleFormatters();
  const dayEvents = events.filter((e) => isSameDay(new Date(e.scheduledStart), day));
  const { startHour, endHour } = hourRangeFor(day, dayEvents);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);

  function offsetFor(date: Date): number {
    return Math.max(0, (date.getHours() - startHour) * 60 + date.getMinutes()) * PX_PER_MIN;
  }

  return (
    <div className="flex overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface">
      <div className="flex flex-col border-r border-border">
        {hours.map((h) => (
          <div key={h} style={{ height: HOUR_HEIGHT }} className="flex items-start justify-end px-2 pt-0.5 text-[10px] text-muted">
            {formatDate(new Date(day.getFullYear(), day.getMonth(), day.getDate(), h), 'h a')}
          </div>
        ))}
      </div>
      <div className="relative flex-1" style={{ height: hours.length * HOUR_HEIGHT }}>
        {hours.map((h, i) => (
          <div key={h} className="absolute inset-x-0 border-t border-border" style={{ top: i * HOUR_HEIGHT }} />
        ))}
        {dayEvents.map((e) => {
          const start = new Date(e.scheduledStart);
          const end = e.scheduledEnd ? new Date(e.scheduledEnd) : new Date(start.getTime() + 60 * 60_000);
          const top = offsetFor(start);
          const height = Math.max(24, offsetFor(end) - top);
          const color = colorForJob(e.jobId);
          return (
            <button
              key={`${e.jobId}-${e.scheduledStart}`}
              type="button"
              onClick={() => onSelectEvent(e)}
              className={cn('absolute left-1 right-1 overflow-hidden rounded-[var(--radius-input)] border-l-2 px-2 py-1 text-left text-[11px]', color.bg, color.text, color.border)}
              style={{ top, height }}
            >
              <div className="truncate font-semibold">{formatDate(start, 'h:mm a')} {e.title}</div>
              {e.installerNames.length ? <div className="truncate opacity-80">{e.installerNames.join(', ')}</div> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
